import { AutocompleteAPI, FetchAPI, FieldAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnCancel, OnChange, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import HealthInsuranceCategory from '../../interfaces/health-insurance-category';
import HealthInsurancePlan from '../../interfaces/health-insurance-plan';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import UserContext from '../../utils/user-context';
import { ContextType } from '@philips-internal/oswald-ui-core';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { VisibilityFieldsResponse } from '../pseudonym-appointments/pseudonym-interfaces';
import { InsuranceFields } from '../../utils/insurance-fields';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';

interface HealthInsurance {
  cdConvenio: string;
  dsConvenio: string;
  ieInsurancePrivate: string;
}

const CategoryFieldVisibility = {
  MANDATORY: 'S',
  NOT_VISIBLE: 'I',
  VISIBLE: 'N'
}

@Controller({ fragment: 'insurance-autocomplete-modal' })
export default class InsuranceAutocompleteModalController extends BaseController {

  @Inject('field-plan')
    fieldPlan!: FieldAPI

  @Inject('field-category')
    fieldCategory!: FieldAPI;

  @Inject('field-checkbox-save-insurance')
    fieldCheckboxSaveInsurace!: FieldAPI;

  @Inject('fetch-category')
    categoryFetch!: FetchAPI;

  @Inject('fetch-plan')
    planFetch!: FetchAPI;

  @Inject('autocomplete-category')
    autoCompleteInsuranceCategory!: AutocompleteAPI;

  @Inject('autocomplete-plan')
    autoCompleteInsurancePlan!: AutocompleteAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('field-health-card-number')
    fieldHealthCardNumber!: FieldAPI;

  @OnMount({target: 'layout-insurances'})
  handleLoadModal(): void {
    const patientId = Number(UserContext.patientId);
    if(patientId === 0) {
      this.fieldCheckboxSaveInsurace.setVisible(false);
    }
  }

  @OnChange({ target: 'autocomplete-health' })
  handleChangeInsurance(event: ChangeEvent<HealthInsurance>): void {
    const autocomplete = event.target as unknown as AutocompleteAPI;
    this.clearAutocompletesOnChange(autocomplete);
    if (event?.value?.cdConvenio) {
      this.context.set({ cdConvenio: event.value.cdConvenio, ieInsurancePrivate: event.value.ieInsurancePrivate });
      if (this.fieldCategory.getVisible()) {
        this.autoCompleteInsuranceCategory.setEnabled(true);
        this.showInsuranceCategory(event.value.cdConvenio, '');
      }
      this.autoCompleteInsurancePlan.setEnabled(true);
      this.showInsurancePlan(event.value.cdConvenio, '');
    } else {
      if (this.fieldCategory.getVisible()) {
        this.autoCompleteInsuranceCategory.setEnabled(false);
      }
      this.autoCompleteInsurancePlan.setEnabled(false);
    }
  }

  @OnChange({ target: 'autocomplete-category' })
  onChangeCategory(event: ChangeEvent<HealthInsuranceCategory>): void {
    if (event?.value) {
      this.showInsurancePlan(event.value.cdConvenio, event.value.cdCategoria);
    } else if (event?.oldValue) {
      this.showInsurancePlan(event.oldValue.cdConvenio, '');
    }
  }

  @OnChange({ target: 'autocomplete-plan' })
  onChangePlan(event: ChangeEvent<HealthInsurancePlan>): void {
    if (!this.fieldCategory.getVisible()) return;
    if (event?.value) {
      this.showInsuranceCategory(event.value.cdConvenio, event.value.cdPlano);
    } else if (event?.oldValue) {
      this.showInsuranceCategory(event.oldValue.cdConvenio, '');
    }
  }

  clearAutocompletesOnChange(autocomplete: AutocompleteAPI): void {
    switch (autocomplete.getAlias()) {
      case 'autocomplete-health':
        this.fieldPlan.setValue({});
        this.fieldCategory.setValue({});
        if (this.fieldCategory.getVisible()) {
          this.autoCompleteInsuranceCategory.setValue({});
        }
        this.autoCompleteInsurancePlan.setValue({});
        break;
      default:
        break;
    }
  }

  showInsuranceCategory(cdConvenio: string, planCode: string): void {
    const tasyInsuranceParameter = this.context.get('tasyInsuranceParameter');
    if (tasyInsuranceParameter != null && (tasyInsuranceParameter === 'N' || tasyInsuranceParameter === 'S')) {
      setTimeout(() => {
        this.categoryFetch.setParameters({ cdConvenio, planCode });
        this.categoryFetch.forceFetch();
      });
    }
  }

  showInsurancePlan(cdConvenio: string, categoryCode: string): void {
    if (cdConvenio) {
      this.autoCompleteInsurancePlan.setEnabled(true);
      setTimeout(() => {
        const validateRules = true;
        this.planFetch.setParameters({ categoryCode, cdConvenio, validateRules});
        this.planFetch.forceFetch();
      });
    }
  }

  @OnSubmit({target: 'form-insurance'})
  handleSubmitFormInsurnace(event: SubmitEvent): void {
    if (event) {
      this.submittableFragmentContext.submit(new SubmitEvent({ record: event.record }));
    }
  }

  @OnCancel({target: 'form-insurance'})
  handleCancelFormInsurance(): void {
    this.submittableFragmentContext.submit(new SubmitEvent());
  }

  @OnMount({target: 'form-insurance'})
  handleMountFormInsurance(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_VISIBILITY_OF_FIELDS).then((response) => {
      const mandatoryFields = response.body as VisibilityFieldsResponse;
      if (mandatoryFields.insuranceCategory === CategoryFieldVisibility.MANDATORY) {
        this.fieldCategory.setValidators([InsuranceFields.required]);
        this.fieldCategory.setVisible(true);
      } else if (mandatoryFields.insuranceCategory === CategoryFieldVisibility.VISIBLE) {
        this.fieldCategory.setVisible(true);
      } else if (mandatoryFields.insuranceCategory === CategoryFieldVisibility.NOT_VISIBLE) {
        this.fieldCategory.setVisible(false);
      }
      if (mandatoryFields.insurancePlan === 'S') {
        this.fieldPlan.setValidators([InsuranceFields.required]);
      }
      if (mandatoryFields.insuranceCardNumber === 'S') {
        this.fieldHealthCardNumber.setValidators([InsuranceFields.required]);
      }
    });
  }

}